/**
 * Diálogo "Registrar dose" (#530, spec D5 e D10). Os campos seguem a ordem em que a pessoa lê a
 * carteira de vacinação: vacina, dose, data, lote, comprovante — e só então o próximo reforço, que
 * já chega preenchido pela data. Encurtá-lo pede motivo; alongá-lo, o servidor recusa.
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AttachFile as AttachFileIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { vaccineTypeService } from '../../../services/vaccineTypeService';
import { vaccinationService } from '../../../services/vaccinationService';
import { getApiErrorMessage } from '../../../types/common';
import type { LinhaVacina, VaccinationPayload, VaccineType } from '../../../types/vaccination';
import { dataBrasileira, somarMeses } from '../../../utils/vaccinationDates';

interface RegisterDoseDialogProps {
  open: boolean;
  employeeId: string;
  /** A ficha já carregada: é dela que sai quais doses do esquema já foram registradas. */
  linhas: readonly LinhaVacina[];
  onClose: () => void;
  onSaved: () => void;
}

type Campo = 'vaccineTypeId' | 'dose' | 'appliedAt' | 'lot' | 'proof' | 'nextDueAt' | 'nextDueReason';
type Erros = Partial<Record<Campo, string>>;

const REFORCO = 'BOOSTER';
const TIPOS_DE_COMPROVANTE = ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'];
const TAMANHO_MAXIMO_DO_COMPROVANTE = 10 * 1024 * 1024;

/**
 * O servidor devolve uma frase só, sem dizer o campo. A ordem importa: "a dose 2 não pode ser
 * anterior à dose 1" fala de data, e "o esquema desta vacina tem 3 doses" fala de dose.
 */
const CAMPO_POR_MENSAGEM: ReadonlyArray<[RegExp, Campo]> = [
  [/comprovante|arquivo/i, 'proof'],
  [/motivo/i, 'nextDueReason'],
  [/próximo reforço/i, 'nextDueAt'],
  [/lote/i, 'lot'],
  [/data de aplicação|anterior à dose|posterior à dose/i, 'appliedAt'],
  [/dose/i, 'dose'],
  [/vacina/i, 'vaccineTypeId'],
];

function campoDaMensagem(mensagem: string): Campo | null {
  return CAMPO_POR_MENSAGEM.find(([padrao]) => padrao.test(mensagem))?.[1] ?? null;
}

function numeroDaDose(dose: string): number | null {
  return dose.startsWith('SCHEME-') ? Number(dose.slice('SCHEME-'.length)) : null;
}

export const RegisterDoseDialog: React.FC<RegisterDoseDialogProps> = ({
  open,
  employeeId,
  linhas,
  onClose,
  onSaved,
}) => {
  const carregarCatalogo = useCallback(() => vaccineTypeService.getAll('ACTIVE'), []);
  const catalogo = useListaPermitida<VaccineType>('vaccine-type.read', 'Vacinas', carregarCatalogo, {
    habilitado: open,
  });

  const [vaccineTypeId, setVaccineTypeId] = useState('');
  const [dose, setDose] = useState('');
  const [appliedAt, setAppliedAt] = useState('');
  const [lot, setLot] = useState('');
  const [comprovante, setComprovante] = useState<File | null>(null);
  // Nulo = a pessoa não mexeu, e vale a sugestão do catálogo para a data escolhida.
  const [proximoInformado, setProximoInformado] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const arquivoRef = useRef<HTMLInputElement>(null);

  const vacina = catalogo.itens.find((item) => String(item.id) === vaccineTypeId);
  const registradas = new Set(
    linhas
      .filter((linha) => String(linha.vaccineType.id) === vaccineTypeId)
      .flatMap((linha) => linha.doses)
      .filter((registrada) => registrada.doseKind === 'SCHEME' && registrada.doseNumber !== null)
      .map((registrada) => registrada.doseNumber)
  );

  const tamanhoDoEsquema = vacina?.scheduleMonths.length ?? 0;
  const opcoesDeDose = [
    ...Array.from({ length: tamanhoDoEsquema }, (_, indice) => indice + 1)
      .filter((numero) => !registradas.has(numero))
      .map((numero) => ({ valor: `SCHEME-${numero}`, rotulo: `${numero}ª dose` })),
    ...(vacina?.boosterMonths ? [{ valor: REFORCO, rotulo: 'Reforço' }] : []),
  ];

  const doseNumber = numeroDaDose(dose);
  const fechaOEsquema = dose === REFORCO || (doseNumber !== null && doseNumber === tamanhoDoEsquema);
  const sugerido =
    vacina?.boosterMonths && fechaOEsquema && appliedAt ? somarMeses(appliedAt, vacina.boosterMonths) : null;
  const proximo = sugerido ? proximoInformado ?? sugerido : null;
  const encurtado = Boolean(sugerido && proximo && proximo < sugerido);

  const limparErro = (campo: Campo) => setErros((atuais) => ({ ...atuais, [campo]: undefined }));

  const escolherComprovante = (evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!arquivo) return;
    if (!TIPOS_DE_COMPROVANTE.includes(arquivo.type)) {
      setErros((atuais) => ({ ...atuais, proof: 'Use PDF, PNG, JPEG ou TIFF.' }));
      return;
    }
    if (arquivo.size > TAMANHO_MAXIMO_DO_COMPROVANTE) {
      setErros((atuais) => ({ ...atuais, proof: 'O comprovante aceita até 10 MB.' }));
      return;
    }
    limparErro('proof');
    setComprovante(arquivo);
  };

  const conferir = (): Erros => {
    const achados: Erros = {};
    if (!vaccineTypeId) achados.vaccineTypeId = 'Escolha a vacina.';
    if (!dose) achados.dose = 'Escolha a dose.';
    if (!appliedAt) achados.appliedAt = 'Informe a data da aplicação.';
    if (sugerido && proximo && proximo > sugerido) {
      achados.nextDueAt = `O próximo reforço não pode passar da data sugerida (${dataBrasileira(sugerido)}).`;
    }
    if (encurtado && !motivo.trim()) achados.nextDueReason = 'Informe o motivo de antecipar o próximo reforço.';
    return achados;
  };

  const registrar = async () => {
    const achados = conferir();
    setErros(achados);
    setErroGeral(null);
    if (Object.keys(achados).length > 0) return;

    const dados: VaccinationPayload = {
      employeeId,
      vaccineTypeId,
      doseKind: dose === REFORCO ? 'BOOSTER' : 'SCHEME',
      doseNumber,
      appliedAt,
      lot: lot.trim() || null,
      nextDueAt: proximo,
      nextDueReason: encurtado ? motivo.trim() : null,
    };

    setGravando(true);
    try {
      await vaccinationService.create(dados, comprovante);
      onSaved();
    } catch (err: unknown) {
      const mensagem = getApiErrorMessage(err, 'Não foi possível registrar a dose.');
      const campo = campoDaMensagem(mensagem);
      if (campo) setErros({ [campo]: mensagem });
      else setErroGeral(mensagem);
    } finally {
      setGravando(false);
    }
  };

  const ajudaDoProximo = sugerido
    ? `Sugerido ${dataBrasileira(sugerido)}${encurtado ? ' · antecipado' : ''}. Pode antecipar, não adiar.`
    : undefined;

  return (
    <Dialog open={open} onClose={gravando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Registrar dose</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {erroGeral && <Alert severity="error">{erroGeral}</Alert>}

          <TextField
            select
            id="dose-vacina"
            label="Vacina"
            value={vaccineTypeId}
            onChange={(evento) => {
              setVaccineTypeId(evento.target.value);
              setDose('');
              setProximoInformado(null);
              limparErro('vaccineTypeId');
            }}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            error={Boolean(erros.vaccineTypeId)}
            helperText={erros.vaccineTypeId ?? catalogo.bloqueio ?? catalogo.erro}
            disabled={gravando}
            fullWidth
          >
            <option value="">{catalogo.carregando ? 'Carregando…' : 'Escolha a vacina'}</option>
            {catalogo.itens.map((item) => (
              <option key={item.id} value={String(item.id)}>
                {item.name}
              </option>
            ))}
          </TextField>

          <TextField
            select
            id="dose-numero"
            label="Dose"
            value={dose}
            onChange={(evento) => {
              setDose(evento.target.value);
              setProximoInformado(null);
              limparErro('dose');
            }}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            error={Boolean(erros.dose)}
            helperText={
              erros.dose ??
              (vacina && opcoesDeDose.length === 0 ? 'Todas as doses do esquema já estão registradas.' : undefined)
            }
            disabled={gravando || !vacina}
            fullWidth
          >
            <option value="">Escolha a dose</option>
            {opcoesDeDose.map((opcao) => (
              <option key={opcao.valor} value={opcao.valor}>
                {opcao.rotulo}
              </option>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              id="dose-data"
              label="Data da aplicação"
              type="date"
              value={appliedAt}
              onChange={(evento) => {
                setAppliedAt(evento.target.value);
                setProximoInformado(null);
                limparErro('appliedAt');
              }}
              InputLabelProps={{ shrink: true }}
              error={Boolean(erros.appliedAt)}
              helperText={erros.appliedAt}
              disabled={gravando}
              fullWidth
            />
            <TextField
              id="dose-lote"
              label="Lote"
              value={lot}
              onChange={(evento) => {
                setLot(evento.target.value);
                limparErro('lot');
              }}
              InputLabelProps={{ shrink: true }}
              error={Boolean(erros.lot)}
              helperText={erros.lot}
              disabled={gravando}
              fullWidth
            />
          </Stack>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Comprovante
            </Typography>
            <input
              ref={arquivoRef}
              type="file"
              aria-label="Comprovante"
              accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
              onChange={escolherComprovante}
              style={{ display: 'none' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => arquivoRef.current?.click()}
                disabled={gravando}
              >
                {comprovante ? 'Trocar comprovante' : 'Anexar comprovante'}
              </Button>
              {comprovante && (
                <>
                  <AttachFileIcon fontSize="small" color="action" />
                  <Typography variant="body2">{comprovante.name}</Typography>
                  <Button size="small" onClick={() => setComprovante(null)} disabled={gravando}>
                    Remover
                  </Button>
                </>
              )}
            </Box>
            <Typography
              variant="caption"
              color={erros.proof ? 'error' : 'text.secondary'}
              sx={{ display: 'block', mt: 0.5 }}
            >
              {erros.proof ?? 'Opcional. PDF, PNG, JPEG ou TIFF, até 10 MB.'}
            </Typography>
          </Box>

          {sugerido && (
            <TextField
              id="dose-proximo"
              label="Próximo reforço"
              type="date"
              value={proximo ?? ''}
              onChange={(evento) => {
                setProximoInformado(evento.target.value || null);
                limparErro('nextDueAt');
              }}
              InputLabelProps={{ shrink: true }}
              error={Boolean(erros.nextDueAt)}
              helperText={erros.nextDueAt ?? ajudaDoProximo}
              disabled={gravando}
              fullWidth
            />
          )}

          {encurtado && (
            <TextField
              id="dose-motivo"
              label="Motivo da antecipação"
              value={motivo}
              onChange={(evento) => {
                setMotivo(evento.target.value);
                limparErro('nextDueReason');
              }}
              InputLabelProps={{ shrink: true }}
              error={Boolean(erros.nextDueReason)}
              helperText={erros.nextDueReason ?? 'Ex.: gestação, acidente com material biológico.'}
              inputProps={{ maxLength: 255 }}
              disabled={gravando}
              required
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={gravando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={registrar}
          disabled={gravando}
          startIcon={gravando ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Registrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterDoseDialog;
